import { defineStore } from "pinia";
import { ref } from "vue";

type ErrorMap = Record<string, string[]>;

const FALLBACK_ERROR = "Неизвестная ошибка";

function toStringList(value: unknown): string[] {
  if (value == null) return [];

  if (typeof value === "string") {
    const text = value.trim();
    return text !== "" ? [text] : [];
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => toStringList(entry));
  }

  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap((entry) =>
      toStringList(entry)
    );
  }

  return [];
}

function normalizeErrorPayload(payload: unknown): ErrorMap {
  if (payload == null) {
    return { error: [FALLBACK_ERROR] };
  }

  if (typeof payload === "string" || Array.isArray(payload)) {
    const messages = toStringList(payload);
    return { error: messages.length > 0 ? messages : [FALLBACK_ERROR] };
  }

  if (typeof payload === "object") {
    const source = payload as Record<string, unknown>;

    // Поддержка текущего формата API: { type: "error", messages: { ... } }
    if (source.messages !== undefined) {
      return normalizeErrorPayload(source.messages);
    }

    // Поддержка Laravel-валидации: { errors: { field: [...] } }
    if (source.errors !== undefined) {
      return normalizeErrorPayload(source.errors);
    }

    const normalized: ErrorMap = {};

    for (const [key, value] of Object.entries(source)) {
      const messages = toStringList(value);
      if (messages.length > 0) {
        normalized[key] = messages;
      }
    }

    if (Object.keys(normalized).length > 0) {
      return normalized;
    }
  }

  return { error: [FALLBACK_ERROR] };
}

export const useNoticeStore = defineStore("notice", () => {
  const id = ref(0);
  const errors = ref<ErrorMap>({});

  const setError = (error: unknown) => {
    errors.value = normalizeErrorPayload(error);
  };

  const getErrors = () => {
    return errors.value;
  };

  const clearErrors = () => {
    errors.value = {};
  };

  return { id, setError, getErrors, clearErrors, errors };
});
